/**
 * ExactSeatOverlay - Native Image Coordinate SVG Overlay
 * Elvis Aloha Collection - Public Ticket Registry
 * 
 * ARCHITECTURE (per user requirements):
 * 1. Store every seat geometry in immutable native chart-image coordinates
 * 2. Create one SVG overlay whose viewBox equals the native chart dimensions
 * 3. Attach that full-image SVG overlay to the OpenSeadragon tiled image
 * 4. Render every seat path inside that SVG using native coordinates only
 * 5. Allow OpenSeadragon to transform the complete SVG with the chart
 * 
 * The SVG path coordinates are native chart coordinates, NOT screen coordinates.
 * OpenSeadragon handles all transformations automatically.
 * 
 * DATA SOURCE: Uses placed_seats collection with seat templates for consistent sizing.
 */

import { useEffect, useRef, useCallback, useState } from 'react';
import OpenSeadragon from 'openseadragon';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

// Default seat styling (can be overridden by template)
const DEFAULT_STYLES = {
  fill: 'rgba(96, 165, 250, 0.55)',
  stroke: '#2563EB',
  strokeWidth: 2,
  hoverFill: 'rgba(96, 165, 250, 0.8)',
  selectedFill: 'rgba(250, 204, 21, 0.85)',
  selectedStroke: '#B45309'
};

const ExactSeatOverlay = ({
  viewer,
  chartId,
  chartDimensions,
  onSeatClick,
  selectedTicketRef
}) => {
  const [seatData, setSeatData] = useState([]);
  const [templates, setTemplates] = useState({});
  const [hoveredId, setHoveredId] = useState(null);
  const overlayRef = useRef(null);
  const svgRef = useRef(null);

  // Fetch placed seats, templates, and match with tickets
  useEffect(() => {
    if (!chartId) return;

    const controller = new AbortController();
    const fetchData = async () => {
      try {
        // CANONICAL SOURCE: seat_geometries + ticket_assignments (display endpoint)
        // Returns ALL persistent seats (incl. unassigned) with display_state + colors.
        const res = await fetch(`${API_BASE}/api/seat-map/seat-geometries/${chartId}?include_assignments=true`, { signal: controller.signal });
        const data = res.ok ? await res.json() : { seats: [] };

        const seats = (data.seats || []).map(s => ({
          id: s.id,
          section: s.section,
          row: s.row,
          seat: s.seat,
          native_x: s.native_x,
          native_y: s.native_y,
          rotation: s.rotation || 0,
          width: s.width || 17,
          height: s.height || 14,
          display_state: s.display_state,
          is_assigned: !!s.is_assigned,
          ticket_id: s.ticket_id || null,
          ticket_reference: s.ticket_reference || null,
          fill_color: s.fill_color,
          stroke_color: s.stroke_color,
        }));

        setSeatData(seats);

        console.log('ExactSeatOverlay (canonical) loaded:', {
          chartId,
          seatCount: seats.length,
          assigned: seats.filter(s => s.is_assigned).length
        });
      } catch (err) {
        if (err.name !== 'AbortError') console.error('ExactSeatOverlay fetch error:', err);
      }
    };

    fetchData();
    return () => controller.abort();
  }, [chartId]);

  // Create or update the SVG overlay
  const setupOverlay = useCallback(() => {
    if (!viewer || !chartDimensions?.width || !chartDimensions?.height) return;

    const tiledImage = viewer.world.getItemAt(0);
    if (!tiledImage) return;

    // Remove existing overlay if any
    if (overlayRef.current) {
      try {
        viewer.removeOverlay(overlayRef.current);
      } catch (e) {
        // Ignore - overlay may not exist
      }
    }

    // Create SVG element with viewBox matching native chart dimensions
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('viewBox', `0 0 ${chartDimensions.width} ${chartDimensions.height}`);
    svg.setAttribute('preserveAspectRatio', 'none');
    svg.style.position = 'absolute';
    svg.style.top = '0';
    svg.style.left = '0';
    svg.style.width = '100%';
    svg.style.height = '100%';
    svg.style.pointerEvents = 'none'; // SVG container doesn't intercept events
    svg.setAttribute('data-testid', 'exact-seat-overlay-svg');
    
    svgRef.current = svg;

    // Create container element for the overlay
    const container = document.createElement('div');
    container.style.position = 'absolute';
    container.style.top = '0';
    container.style.left = '0';
    container.style.width = '100%';
    container.style.height = '100%';
    container.style.pointerEvents = 'none';
    container.appendChild(svg);
    
    overlayRef.current = container;

    // Convert full image bounds to viewport rectangle
    const imageRect = tiledImage.imageToViewportRectangle(
      0, 0, chartDimensions.width, chartDimensions.height
    );

    // Add as OpenSeadragon overlay - it will transform with the image
    viewer.addOverlay({
      element: container,
      location: new OpenSeadragon.Rect(
        imageRect.x,
        imageRect.y,
        imageRect.width,
        imageRect.height
      )
    });

    // Render seat paths
    renderSeatPaths();

  }, [viewer, chartDimensions, seatData, selectedTicketRef, hoveredId]);

  // Render seat paths in native coordinates
  const renderSeatPaths = useCallback(() => {
    const svg = svgRef.current;
    if (!svg) return;

    // Clear existing paths
    while (svg.firstChild) {
      svg.removeChild(svg.firstChild);
    }

    seatData.forEach((s) => {
      const { id, native_x, native_y, rotation, width, height } = s;

      // Top-left corner (native_x/y is center)
      const x = native_x - width / 2;
      const y = native_y - height / 2;

      // Group for this seat (visible + hit area), rotated in native coords
      const group = document.createElementNS('http://www.w3.org/2000/svg', 'g');
      group.setAttribute('data-seat-id', id);
      group.setAttribute('data-ticket-ref', s.ticket_reference || '');
      group.setAttribute('data-display-state', s.display_state || 'unassigned');
      group.setAttribute('transform', `rotate(${rotation || 0}, ${native_x}, ${native_y})`);

      const isSelected = s.ticket_reference && s.ticket_reference === selectedTicketRef;
      const isHovered = id === hoveredId;

      // Colors: server provides status color; selection/hover override outline
      const fillColor = isSelected
        ? DEFAULT_STYLES.selectedFill
        : (s.fill_color || DEFAULT_STYLES.fill);
      const strokeColor = isSelected
        ? DEFAULT_STYLES.selectedStroke
        : (s.stroke_color || DEFAULT_STYLES.stroke);
      const strokeWidth = isSelected ? 2.5 : (isHovered ? 2.2 : DEFAULT_STYLES.strokeWidth);

      // VISIBLE PATH - exact seat footprint in native coords
      const visibleRect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
      visibleRect.setAttribute('x', x);
      visibleRect.setAttribute('y', y);
      visibleRect.setAttribute('width', width);
      visibleRect.setAttribute('height', height);
      visibleRect.setAttribute('fill', fillColor);
      visibleRect.setAttribute('stroke', strokeColor);
      visibleRect.setAttribute('stroke-width', strokeWidth);
      visibleRect.setAttribute('rx', '1');
      visibleRect.style.pointerEvents = 'none';
      if (isHovered) visibleRect.setAttribute('opacity', '0.9');

      // HIT AREA - slightly larger for touch, transparent, clipped to seat
      const hitPadding = 2;
      const hitRect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
      hitRect.setAttribute('x', x - hitPadding);
      hitRect.setAttribute('y', y - hitPadding);
      hitRect.setAttribute('width', width + hitPadding * 2);
      hitRect.setAttribute('height', height + hitPadding * 2);
      hitRect.setAttribute('fill', 'transparent');
      hitRect.setAttribute('stroke', 'none');
      hitRect.style.pointerEvents = 'all';
      hitRect.style.cursor = 'pointer';

      hitRect.addEventListener('mouseenter', () => setHoveredId(id));
      hitRect.addEventListener('mouseleave', () => setHoveredId(null));
      const triggerClick = () => {
        const ticketLike = s.is_assigned
          ? { registry_reference: s.ticket_reference, id: s.ticket_id, section: s.section, row: s.row, seat: s.seat }
          : null;
        onSeatClick?.(ticketLike, s);
      };
      hitRect.addEventListener('click', (e) => {
        e.stopPropagation();
        e.preventDefault();
        triggerClick();
      });
      hitRect.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          triggerClick();
        }
      });
      hitRect.setAttribute('tabindex', '0');
      hitRect.setAttribute('class', 'exact-seat-hit');

      // Accessible tooltip + status text
      const stateLabel = s.is_assigned
        ? (s.display_state === 'assigned_verified' ? 'verified ticket assigned' : 'assignment pending')
        : 'unassigned';
      const title = document.createElementNS('http://www.w3.org/2000/svg', 'title');
      title.textContent = `${s.section} Row ${s.row} Seat ${s.seat}, ${stateLabel}`;
      hitRect.appendChild(title);
      hitRect.setAttribute('role', 'button');
      hitRect.setAttribute('aria-label', `${s.section} Row ${s.row} Seat ${s.seat}, ${stateLabel}`);

      group.appendChild(visibleRect);
      group.appendChild(hitRect);

      svg.appendChild(group);
    });

  }, [seatData, selectedTicketRef, hoveredId, onSeatClick]);

  // Initialize overlay when viewer opens
  useEffect(() => {
    if (!viewer) return;

    const handleOpen = () => {
      const tiledImage = viewer.world.getItemAt(0);
      if (tiledImage) {
        setupOverlay();
      }
    };

    // If already open, set up immediately
    if (viewer.isOpen && viewer.isOpen()) {
      handleOpen();
    }

    viewer.addHandler('open', handleOpen);
    const handleTileLoaded = () => {
      if (!overlayRef.current && viewer.world.getItemAt(0)) {
        setupOverlay();
      }
    };
    viewer.addHandler('tile-loaded', handleTileLoaded);

    return () => {
      viewer.removeHandler('open', handleOpen);
      viewer.removeHandler('tile-loaded', handleTileLoaded);
      if (overlayRef.current) {
        try {
          viewer.removeOverlay(overlayRef.current);
        } catch (e) {
          // Ignore - overlay may not exist
        }
      }
    };
  }, [viewer, setupOverlay]);

  // Re-render paths when seat data or selection changes
  useEffect(() => {
    renderSeatPaths();
  }, [renderSeatPaths, seatData, selectedTicketRef, hoveredId]);

  // This component doesn't render anything directly - it manages OSD overlay
  return null;
};

export default ExactSeatOverlay;
